/**
 * 設定頁模組 registry（REQ-20260419003）。
 *
 * 所有在 `/settings/*` 下的子功能都在此登記。設計目的：
 * - Router 表可由此動態生成
 * - 左側 nav 可依使用者權限過濾顯示
 * - 未來接上 per-module permission 時，只需把 allowedRoles 換成 permission key
 */
import type { ReactElement } from 'react';
import { Role } from '@app/shared';
import { SettingsTable } from './SettingsTable';
import { SmtpForm } from './SmtpForm';
import { CompanyProfileForm } from './CompanyProfileForm';
import { EinvoiceForm } from './EinvoiceForm';
import { AiProviderForm } from './AiProviderForm';
import { OpenAiImageProviderForm } from './OpenAiImageProviderForm';
import { TelegramForm } from './TelegramForm';
import { AiFollowupForm } from './AiFollowupForm';
import { LineForm } from './LineForm';
import { MdbImportPage } from './MdbImportPage';
import { SystemBackupPage } from './SystemBackupPage';
import { SmartAdjustSettingsPage } from './SmartAdjustSettingsPage';
import { UserListPage } from '../../pages/UserListPage';
import { UserNewPage } from '../../pages/UserNewPage';
import { UserEditPage } from '../../pages/UserEditPage';
import { AuditLogPage } from '../../pages/AuditLogPage';

export interface SettingsModule {
  id: string;
  /** 相對 `/settings` 的子路徑，例：`users`, `users/new`, `users/:id/edit`。 */
  path: string;
  /** 左側 nav 顯示名稱；`null` 代表不顯示於 nav（例如 users/new）。 */
  navLabel: string | null;
  /** Nav 分組：`data` 資料字典類 / `admin` 管理類。分組用於 UI 分隔線。 */
  navGroup: 'data' | 'admin';
  /** 該模組可存取的角色（未來可改為 permission key）。 */
  allowedRoles: Role[];
  element: ReactElement;
}

export const SETTINGS_MODULES: SettingsModule[] = [
  // ─── 資料字典類 ─────────────────────────────
  {
    id: 'industries',
    path: 'industries',
    navLabel: '行業類別',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="industries" label="行業類別" />,
  },
  {
    id: 'interview-types',
    path: 'interview-types',
    navLabel: '訪談類別',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="interview-types" label="訪談類別" />,
  },
  {
    id: 'payment-certs',
    path: 'payment-certs',
    navLabel: '收款憑證',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="payment-certs" label="收款憑證" />,
  },
  {
    id: 'payment-methods',
    path: 'payment-methods',
    navLabel: '收款方式',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="payment-methods" label="收款方式" />,
  },
  // REQ-20260519 ERP Phase 1A:進銷存所需的 4 個 reference tables
  {
    id: 'product-categories',
    path: 'product-categories',
    navLabel: '貨品類別',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="product-categories" label="貨品類別" />,
  },
  {
    id: 'product-units',
    path: 'product-units',
    navLabel: '貨品單位',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="product-units" label="貨品單位" />,
  },
  {
    id: 'supplier-categories',
    path: 'supplier-categories',
    navLabel: '廠商類別',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="supplier-categories" label="廠商類別" />,
  },
  {
    id: 'warehouses',
    path: 'warehouses',
    navLabel: '倉庫',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="warehouses" label="倉庫" />,
  },
  // REQ-20260810001:藝品印刷訂單四個現場欄位的常用選項(建應收單時可複選 / 手動輸入)
  {
    id: 'processing-options',
    path: 'processing-options',
    navLabel: '印刷加工',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="processing-options" label="印刷加工" />,
  },
  {
    id: 'print-color-options',
    path: 'print-color-options',
    navLabel: '印刷顏色',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="print-color-options" label="印刷顏色" />,
  },
  // REQ-20260810003:印刷面別(單面 / 雙面)。同一組現場欄位,但建單時是單選
  {
    id: 'print-side-options',
    path: 'print-side-options',
    navLabel: '印刷面別',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="print-side-options" label="印刷面別" />,
  },
  {
    id: 'artwork-handling-options',
    path: 'artwork-handling-options',
    navLabel: '稿件處理',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="artwork-handling-options" label="稿件處理" />,
  },
  {
    id: 'shipping-method-options',
    path: 'shipping-method-options',
    navLabel: '出貨方式',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="shipping-method-options" label="出貨方式" />,
  },
  // REQ-20260814002:進貨類別。與上面幾筆不同,進貨單存的是 **code**,
  // 所以列表 / 列印頁顯示時要拿 code 回來對照名稱(見 `referenceNameMap()`)
  {
    id: 'purchase-categories',
    path: 'purchase-categories',
    navLabel: '進貨類別',
    navGroup: 'data',
    allowedRoles: [Role.ADMIN],
    element: <SettingsTable path="purchase-categories" label="進貨類別" />,
  },
  // ─── 管理類 ──────────────────────────────────
  // REQ-20260817004:我方公司抬頭。放在管理類第一筆 —— 這是所有單據都會印出去的
  // 對外資料,不是某個功能的開關
  {
    id: 'company-profile',
    path: 'company-profile',
    navLabel: '公司基本資料',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <CompanyProfileForm />,
  },
  {
    id: 'smtp',
    path: 'smtp',
    navLabel: 'SMTP 寄信',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <SmtpForm />,
  },
  // REQ-20260602 電子發票;REQ-20260915003 起改串 ezPay(字軌由 ezPay 管,已無「發票字軌」頁)
  {
    id: 'einvoice',
    path: 'einvoice',
    navLabel: '電子發票',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <EinvoiceForm />,
  },
  {
    id: 'ai-provider',
    path: 'ai-provider',
    navLabel: 'AI 提供商',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <AiProviderForm />,
  },
  {
    id: 'openai-image-provider',
    path: 'openai-image-provider',
    navLabel: 'OpenAI 生圖',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <OpenAiImageProviderForm />,
  },
  {
    id: 'telegram',
    path: 'telegram',
    navLabel: 'Telegram 推播',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <TelegramForm />,
  },
  {
    id: 'ai-followup',
    path: 'ai-followup',
    navLabel: 'AI 每日追蹤',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <AiFollowupForm />,
  },
  {
    id: 'line',
    path: 'line',
    navLabel: 'LINE 設定',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <LineForm />,
  },
  {
    id: 'users',
    path: 'users',
    navLabel: '使用者',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <UserListPage />,
  },
  {
    id: 'users-new',
    path: 'users/new',
    navLabel: null,
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <UserNewPage />,
  },
  {
    id: 'users-edit',
    path: 'users/:id/edit',
    navLabel: null,
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <UserEditPage />,
  },
  {
    id: 'audit-log',
    path: 'audit-log',
    navLabel: '稽核日誌',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <AuditLogPage />,
  },
  // REQ-20260520 ERP:mdb 匯入工具
  {
    id: 'mdb-import',
    path: 'mdb-import',
    navLabel: '舊系統匯入(mdb)',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <MdbImportPage />,
  },
  // REQ-20260522:系統備份
  {
    id: 'system-backup',
    path: 'system-backup',
    navLabel: '系統備份',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <SystemBackupPage />,
  },
  // REQ-20260523:我獨自升級設定(整合設定;主畫面在 top nav /smart-adjust)
  {
    id: 'smart-adjust-settings',
    path: 'smart-adjust-settings',
    navLabel: '我獨自升級設定',
    navGroup: 'admin',
    allowedRoles: [Role.ADMIN],
    element: <SmartAdjustSettingsPage />,
  },
];

/** 使用者可存取的模組（nav + guard 共用）。 */
export function accessibleModules(role: Role | undefined): SettingsModule[] {
  if (!role) return [];
  return SETTINGS_MODULES.filter((m) => m.allowedRoles.includes(role));
}

/**
 * 給定 role，回該使用者進 `/settings` 時應預設載入的模組 path。
 * 規則：第一個 navLabel 非 null 的可存取模組（保證是 nav 上會出現的那一個）。
 */
export function defaultSettingsModule(role: Role | undefined): string | null {
  const first = accessibleModules(role).find((m) => m.navLabel != null);
  return first ? first.path : null;
}
